import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { omitBy } from "lodash";
import { toast } from "react-hot-toast";
import { UserProfile } from "~/lib/drizzle/type";
import { UserSettings } from "~/lib/drizzle/user_profile";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";

interface UpdateUserPayload {
    email?: string;
    name?: string;
    phone?: string;
    company?: string;
    image?: string | null;
    settings?: UserSettings;
    disableToast?: boolean;
    inactive_from?: null | string;
    inactive_to?: null | string;
    autoreply_message?: string | null;
}

// TODO: To replace with v2 API
export const useGetUsers = (): UseQueryResult<UserProfile[]> => {
    const queryFn = async () => {
        const result = await fetchWrapper<UserProfile[]>({
            url: `/api/team`,
            method: 'GET',
        });

        const data = (result?.data ?? []).filter((item) => !item?.user_status);

        return data
    }

    return useQuery({
        queryKey: ['getUsers'],
        queryFn,
    })
}

export const useGetUserProfile = (refetchInterval?: number): UseQueryResult<UserProfile | null> => {
    const { getToken } = useAuth();

    return useQuery({
        queryKey: ['userProfile', refetchInterval],
        queryFn: async () => {
            const token = await getToken();
            return fetchWrapper({
                url: `${apiBaseUrl}/user-profiles/me`,
                method: 'GET',
                token,
            })
        },
        staleTime: 5_000,
        refetchInterval,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    })
}

export const useUpdateUserMutation = (props?: BaseProps) => {
    const query = useQueryClient();
    const { getToken } = useAuth();

    const mutationFn = async (data: UpdateUserPayload) => {
        const token = await getToken()
        return fetchWrapper<Partial<UserProfile>>({
            url: `${apiBaseUrl}/user-profiles/me`,
            body: omitBy(data, val => val === undefined),
            method: 'PATCH',
            token,
            showSuccessToast: true,
            throwOnError: true,
            showErrorToast: false,
        })
    }

    return useMutation({
        mutationFn,
        onSuccess: async (data, variable) => {
            await query.invalidateQueries({ queryKey: ['userProfile'] });
            if (!variable.disableToast) toast.success('User updated successfully');
            props?.onSuccess?.()
        },
        onError: props?.onError,
    })
}

export const useResetPasswordMutation = () => {
    const query = useQueryClient();
    const { getToken } = useAuth();

    const mutationFn = async (data: { password: string }) => {
        const token = await getToken()
        return fetchWrapper({
            url: `${apiBaseUrl}/user-profiles/me/reset-password`,
            body: data,
            method: 'PATCH',
            token,
        })
    }

    return useMutation({
        mutationFn,
        onSuccess: async (data, variable) => {
            toast.success('Password reset successfully');
        }
    })
}

export const useInviteUserMutation = (props: BaseProps) => {
    const { getToken } = useAuth()
    const mutationFn = async (payload: object) => {
        const token = await getToken()
        const endpoint = `${apiBaseUrl}/user-profiles/invite`
        await fetchWrapper({
            url: endpoint,
            method: 'POST',
            body: payload,
            token,
            showToast: false,
        })
    }

    const mutation = useMutation({
        mutationFn,
        mutationKey: ['inviteUser'],
        onSuccess: props.onSuccess,
        onError: props.onError
    })

    return {
        isLoading: mutation.isLoading,
        inviteUser: mutation.mutateAsync
    }
}


export const useDeleteUser = (props: BaseProps) => {
    const { getToken } = useAuth()
    const mutationFn = async (payload: {
        id: string
    }) => {
        const token = await getToken()
        const endpoint = `${apiBaseUrl}/user-profiles/${payload.id}`
        return await fetchWrapper({
            url: endpoint,
            method: 'DELETE',
            token,
            showToast: false,
        })
    };

    const mutation = useMutation({
        mutationKey: ['deleteUser'],
        mutationFn,
        onSuccess: props.onSuccess,
        onError: props.onError,
    })

    return {
        isLoading: mutation.isLoading,
        deleteUser: mutation.mutateAsync,
    }
}

